<template>
  <div v-if="show" class="go-to-v2-banner" role="region" aria-label="Migration to the new Fresh platform">
    <div class="go-to-v2-banner__text">
      <span class="go-to-v2-banner__icon"><feather-icon icon="CheckIcon" svgClasses="w-4 h-4" /></span>
      <span>
        <strong>Your clinic has moved to the new Fresh platform</strong>
        <span class="go-to-v2-banner__subtext">Continue there for your day-to-day clinic work.</span>
      </span>
    </div>
    <div class="go-to-v2-banner__actions">
      <button ref="details" type="button" class="go-to-v2-banner__details" @click="openNotice">
        Migration details
      </button>
      <a :href="v2Url" class="go-to-v2-banner__cta">
        Go to new platform
        <feather-icon icon="ArrowUpRightIcon" svgClasses="w-4 h-4" />
      </a>
    </div>

    <migration-notice-modal
      v-if="noticeOpen"
      :v2-url="v2Url"
      @close="closeNotice"
    />
  </div>
</template>

<script>
import MigrationNoticeModal from './MigrationNoticeModal.vue';
import { getActiveMigrationTarget } from '@/utils/checkOrgMigration';
import { resolveV2Url } from '@/utils/goToV2Target';
import {
  dismissMigrationNotice,
  isMigrationNoticeDismissed,
  showsMigrationTreatment,
  storedUserId,
} from '@/utils/migrationNotice';

// The notice opens once per user and active org; after it is closed this
// banner stays up and "Migration details" reopens it (COR-6465).
export default {
  name: 'GoToV2Banner',
  components: { MigrationNoticeModal },
  data() {
    return {
      migrationTarget: getActiveMigrationTarget(),
      noticeOpen: false,
    };
  },
  computed: {
    isLoggedIn() {
      return (
        localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}LoggedIn`) === 'true'
      );
    },
    v2Url() {
      return resolveV2Url(this.migrationTarget.orgPlatformUrl, process.env.VUE_APP_REGION);
    },
    show() {
      return this.isLoggedIn && showsMigrationTreatment(this.migrationTarget, this.$route.path, this.v2Url);
    },
  },
  watch: {
    // Re-read the active org on every navigation, not just on first load.
    $route: {
      immediate: true,
      handler() {
        this.migrationTarget = getActiveMigrationTarget();
        if (!this.show) {
          this.noticeOpen = false;
          return;
        }
        if (!isMigrationNoticeDismissed(storedUserId(), this.migrationTarget.orgId)) {
          this.noticeOpen = true;
        }
      },
    },
  },
  methods: {
    openNotice() {
      this.noticeOpen = true;
    },
    closeNotice() {
      dismissMigrationNotice(storedUserId(), this.migrationTarget.orgId);
      this.noticeOpen = false;
      this.$nextTick(() => {
        if (this.$refs.details) this.$refs.details.focus();
      });
    },
  },
};
</script>

<style scoped>
.go-to-v2-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 1.5rem;
  padding: 12px 16px;
  background: #eef6f1;
  border: 1px solid #d3e3da;
  border-radius: 8px;
  color: #0e4333;
}
.go-to-v2-banner__text {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  line-height: 1.4;
}
.go-to-v2-banner__text strong {
  display: block;
}
.go-to-v2-banner__subtext {
  font-size: 0.85rem;
  color: #5b6b65;
}
.go-to-v2-banner__icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #d9ebe1;
}
.go-to-v2-banner__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}
.go-to-v2-banner__details {
  padding: 0;
  border: 0;
  background: none;
  color: #0e4333;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.go-to-v2-banner__details:hover {
  text-decoration: underline;
}
.go-to-v2-banner__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 6px;
  background: #0e4333;
  color: #fff;
  font-weight: 600;
}
.go-to-v2-banner__cta:hover,
.go-to-v2-banner__cta:focus {
  background: #145c46;
  color: #fff;
}
.go-to-v2-banner__details:focus-visible,
.go-to-v2-banner__cta:focus-visible {
  outline: 3px solid #84cdb8;
  outline-offset: 2px;
}
</style>
